import { subject } from "@casl/ability";
import axios, { AxiosRequestConfig } from "axios";

import { createFilterParams } from "../../../api/helpers/createFilterParams";
import getErrorDetails from "../../../api/helpers/getErrorDetails";
import {
  getPaginationInfoFromHeaders,
  PaginatedList,
} from "../../../api/helpers/pagination";
import paramsSerializer from "../../../api/helpers/paramsSerializer";
import { portalApi, TAGS } from "../../../api/rtkApi";
import { DEFAULT_PAGINATION } from "../../../constants/defaultPagination";
import { SUBJECT_PAYMENT } from "../../../models/role/subject";
import { transformPaymentResponse } from "../utils/transformPaymentResponse";

import type { UUID } from "../../../utils";
import type { ObjectWithId } from "../../../utils/ObjectWithId";
import type {
  default as Payment,
  PaymentsQueryParams,
} from "../models/Payment";
import type { PaymentRefundStatus, Refund } from "../models/Refund";

type PaymentResponse = Payment;
type PaymentQuery = ObjectWithId;
type GetPaymentRefundsQuery = {
  paymentId: UUID;
};
type PostPaymentRefundQuery = ObjectWithId & {
  amount: number;
  code: string;
};
type PostPaymentRefundQueryResponse = ObjectWithId;
type GetPaymentRefundStatusQuery = ObjectWithId;
type GetPaymentRefundStatusResponse = PaymentRefundStatus;

export async function getPayments(
  qsParams: PaymentsQueryParams & {
    orderBy: any;
  },
  options?: AxiosRequestConfig
): Promise<PaginatedList<Payment>> {
  const params = createFilterParams(qsParams, { pagination: true });

  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: "/payments",
      params: {
        ...params,
        page: qsParams.page,
      },
      paramsSerializer,
    });
    const { data = {}, headers: responseHeaders = {} } = response;

    const {
      totalItems,
      currentPage,
      currentLimit,
    } = getPaginationInfoFromHeaders(responseHeaders);

    return {
      list: (data ?? []).map((payment: Payment) =>
        subject(SUBJECT_PAYMENT, payment)
      ),
      totalItems,
      currentPage: currentPage + 1,
      currentLimit,
    };
  } catch (error) {
    throw getErrorDetails(error);
  }
}

const extendedApi = portalApi.injectEndpoints({
  endpoints: (builder) => ({
    getPayment: builder.query<PaymentResponse, PaymentQuery>({
      query: ({ id }) => `payments/${id}`,
      keepUnusedDataFor: 30,
      providesTags: (_, __, { id }) => [{ type: TAGS.PAYMENT, id }],
      transformResponse: transformPaymentResponse,
    }),
    getPaymentRefundStatus: builder.query<
      GetPaymentRefundStatusResponse,
      GetPaymentRefundStatusQuery
    >({
      query: ({ id }) => ({
        url: `payments/${id}/refund-status`,
      }),
      providesTags: (_, __, { id }) => [{ type: TAGS.REFUND_STATUS, id }],
    }),
    getPaymentRefunds: builder.query<Refund[], GetPaymentRefundsQuery>({
      query: ({ paymentId }) => ({
        url: `payments/${paymentId}/refunds`,
        params: DEFAULT_PAGINATION,
      }),
      transformResponse: (refunds: Refund[]) =>
        refunds.sort(
          ({ createdAt: dateA }, { createdAt: dateB }) =>
            new Date(dateB).getTime() - new Date(dateA).getTime()
        ),
      providesTags: [TAGS.REFUNDS],
    }),
    postPaymentRefund: builder.mutation<
      PostPaymentRefundQueryResponse,
      PostPaymentRefundQuery
    >({
      query: ({ amount, code, id }) => ({
        url: `/payments/${id}/request-refund`,
        method: "POST",
        body: { amount, code },
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: TAGS.PAYMENT, id },
        { type: TAGS.REFUND_STATUS, id },
        TAGS.REFUNDS,
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPaymentQuery,
  useGetPaymentRefundsQuery,
  useGetPaymentRefundStatusQuery,
  usePostPaymentRefundMutation,
} = extendedApi;
